import React, { useState, useEffect } from "react";
import {
  Layout,
  Menu,
  Avatar,
  Tag,
  Dropdown,
  message,
  Button,
  Badge,
  Spin } from
"antd";
import {
  DashboardOutlined,
  BookOutlined,
  TrophyOutlined,
  ToolOutlined,
  TeamOutlined,
  CalendarOutlined,
  FileTextOutlined,
  ProjectOutlined,
  SolutionOutlined,
  CheckSquareOutlined,
  DollarOutlined,
  NotificationOutlined,
  BellOutlined,
  CarryOutOutlined,
  ReadOutlined,
  UserOutlined,



  ClockCircleOutlined,
  QuestionCircleOutlined,

  LogoutOutlined,
  SettingOutlined,
  ApiOutlined,
  DesktopOutlined,
  RobotOutlined,
  HomeOutlined,

  InfoCircleOutlined,
  BankOutlined,
  UsergroupAddOutlined,
  ApartmentOutlined,
  NodeIndexOutlined,
  FacebookOutlined,
  InstagramOutlined,
  LinkedinOutlined,
  TwitterOutlined,
  YoutubeOutlined,
  ExportOutlined,
  MailOutlined,
  MessageOutlined,
  EyeOutlined,
  PictureOutlined,
  EditOutlined,
  UserSwitchOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CloseOutlined,
  LoadingOutlined,
  KeyOutlined,
  AudioOutlined } from
"@ant-design/icons";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuthDispatch } from "../../context/AuthContext";
import { useSidebar } from "../../context/SidebarContext";
import Cookies from "universal-cookie";
import { cookieOptions } from "../../utils/cookieConfig";
import logoImage from "../../assets/logo-mediatropy-2024-name.gif";
import faviconImage from "../../assets/favicon-mt.png";
import nodeAPI from "../../service/ApiServiceNode";
import { onLeaderChange } from "../../utils/tabLeader";
import { POLL_TIMEOUT } from "../../utils/axiosRetryConfig";
import { MT_CAREER, USER_STATUS } from "../../constants";
import roleService, { ROLES } from "../../service/RoleService";
import HelpFeedbackButton from "../HelpFeedback/HelpFeedbackButton";
import HelpFeedbackModal from "../HelpFeedback/HelpFeedbackModal";
import ScreenQuickNav from "../Common/ScreenQuickNav";
import VoiceAgent from "../../voice/VoiceAgent";
import { HOME_SIDEBAR_MAIN_KEYS } from "./homeSidebarMenuConfig";
import {
  getHomeSiderAdminMenuOpenKeys,
  getHomeSiderAdminSelectedKey,
  getHomeSiderMainMenuOpenKeys,
  getHomeSiderMainMenuSelectedKeys,
  getHomeSiderMainSelectedKey,
  getHomeSiderMenuMode,
  getHomeSiderRouteOpenKeys
} from "./homeSiderSelection";
import logger from "../../utils/logger";



const { Sider } = Layout;
const MOBILE_SIDEBAR_QUERY = "(max-width: 768px)";

const getIsMobileSidebar = () =>
  typeof window !== "undefined" && window.matchMedia(MOBILE_SIDEBAR_QUERY).matches;

const canManageWikiSettings = () => {
  const directRoles = roleService.getUserRoles();
  return [
    ROLES.ROLE_SUPER_ADMIN,
    ROLES.ROLE_OWNER,
    ROLES.ROLE_HR
  ].some((role) => directRoles.includes(role));
};

const HomeSider = ({
  collapsed,
  animationEnabled,
  userRoles: _userRoles,
  userName,
  workStatus,
  setWorkStatus
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAuthDispatch();
  const cookies = new Cookies();
  const { toggleCollapsed, setCollapsed } = useSidebar();
  const [profilePicture, setProfilePicture] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [voiceAssistantVisible, setVoiceAssistantVisible] = useState(false);
  const [isMobileSidebar, setIsMobileSidebar] = useState(getIsMobileSidebar);
  const [fetchedOfficeWidget, setFetchedOfficeWidget] = useState(null);
  const voiceAssistantEnabled = process.env.VITE_VOICE_ASSISTANT_ENTRY_POINT === "true";
  const menuMode = getHomeSiderMenuMode(isMobileSidebar);
  const [openKeys, setOpenKeys] = React.useState(() =>
    getHomeSiderRouteOpenKeys({
      path: location.pathname,
      collapsed,
      isMobileSidebar
    })
  );
  const [menuInstanceKey, resetMenuInstance] = React.useReducer((key) => key + 1, 0);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia(MOBILE_SIDEBAR_QUERY);
    const updateMobileSidebar = () => setIsMobileSidebar(mediaQuery.matches);

    updateMobileSidebar();
    mediaQuery.addEventListener("change", updateMobileSidebar);

    return () => {
      mediaQuery.removeEventListener("change", updateMobileSidebar);
    };
  }, []);

  // Fetch user profile on mount, and set up cross-tab notification polling
  // Only the leader tab polls; follower tabs receive counts via BroadcastChannel
  useEffect(() => {
    fetchUserProfile();
    fetchNotificationCount();

    let notificationInterval = null;
    let broadcastChannel = null;

    try {
      if (typeof BroadcastChannel !== 'undefined') {
        broadcastChannel = new BroadcastChannel('notification-count-sync');
        broadcastChannel.onmessage = (event) => {
          if (event.data && typeof event.data.count === 'number') {
            setNotificationCount(event.data.count);
          }
        };
      }
    } catch (_e) {/* BroadcastChannel not supported — each tab polls independently */}

    const startPolling = () => {
      stopPolling();
      notificationInterval = setInterval(() => {
        fetchNotificationCount().then((count) => {
          // Broadcast count to follower tabs
          if (broadcastChannel && typeof count === 'number') {
            try {broadcastChannel.postMessage({ count });} catch (_e) {/* ignore */}
          }
        });
      }, 30000);
    };

    const stopPolling = () => {
      if (notificationInterval) {
        clearInterval(notificationInterval);
        notificationInterval = null;
      }
    };

    // Leader election: only leader tab polls
    const unsubLeader = onLeaderChange((amLeader) => {
      if (amLeader && !document.hidden) {
        startPolling();
      } else {
        stopPolling();
      }
    });

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        fetchNotificationCount();
        // onLeaderChange will restart polling if we're the leader
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopPolling();
      unsubLeader();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (broadcastChannel) {
        try {broadcastChannel.close();} catch (_e) {/* ignore */}
      }
    };
  }, []);

  useEffect(() => {
    const currentUser = roleService.getCurrentUser();
    const officeValue = currentUser?.office;
    const cookieOfficeWidget =
      officeValue && typeof officeValue === "object" ? officeValue.widget : null;
    const officeId =
      currentUser?.office_id ||
      (officeValue && typeof officeValue === "object" ? officeValue.id : officeValue);
    const numericOfficeId = Number(officeId);

    if (cookieOfficeWidget || !Number.isFinite(numericOfficeId) || numericOfficeId <= 0) {
      setFetchedOfficeWidget(null);
      return undefined;
    }

    let isActive = true;

    nodeAPI
      .get(`/company-directory/office/${numericOfficeId}`)
      .then((response) => {
        if (!isActive) return;
        const officeData = response.data?.data || response.data;
        setFetchedOfficeWidget(officeData?.widget || null);
      })
      .catch((error) => {
        logger.error("Failed to fetch office widget:", error);
      });

    return () => {
      isActive = false;
    };
  }, []);

  // Desktop submenus are popup menus and must close after route changes.
  // Mobile uses inline sections, so the active route stays expanded there.
  useEffect(() => {
    setOpenKeys(getHomeSiderRouteOpenKeys({
      path: location.pathname,
      collapsed,
      isMobileSidebar
    }));
  }, [location.pathname, location.search, collapsed, isMobileSidebar]);

  const fetchUserProfile = async () => {
    try {
      setLoadingProfile(true);
      const response = await nodeAPI.get("/user/profile");
      if (response.data && response.data.data) {
        setProfilePicture(response.data.data.profilePicture);
      }
    } catch (error) {
      logger.error("Failed to fetch user profile:", error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchNotificationCount = async () => {
    try {
      const response = await nodeAPI.get("/notification/count", { timeout: POLL_TIMEOUT });
      if (response.data && response.data.data) {
        const count = response.data.data.count || 0;
        setNotificationCount(count);
        return count;
      }
    } catch (error) {
      logger.error("Failed to fetch notification count:", error);
      setNotificationCount(0);
    }
    return 0;
  };

  // Check if we're in production environment
  const isProduction = () => {
    // Only check REACT_APP_ENV to respect Netlify configuration
    // This allows us to set development mode even in production builds
    return process.env.VITE_ENV === 'production';
  };

  // Get the base URL for external links (only used in production)
  const getExternalBaseUrl = () => {
    return 'https://hr.mediatropy.com';
  };

  const resolveExternalUrl = (externalUrl) => {
    const value = String(externalUrl || "").trim();
    if (!value) {
      return "#";
    }
    if (/^[a-z][a-z0-9+.-]*:/i.test(value)) {
      return value;
    }
    if (/^\/\//.test(value)) {
      return `https:${value}`;
    }
    if (/^[\w.-]+\.[a-z]{2,}(?::\d+)?(?:[/?#]|$)/i.test(value)) {
      return `https://${value}`;
    }
    return isProduction() ? `${getExternalBaseUrl()}${value}` : value;
  };

  const currentUser = roleService.getCurrentUser();
  const currentOffice = currentUser?.office;
  const officeWidget =
    (currentOffice && typeof currentOffice === "object" ? currentOffice.widget : null) ||
    fetchedOfficeWidget;
  const vacanciesVisible = officeWidget?.job_visibility !== false;
  const vacanciesLink = MT_CAREER;
  const payslipVisible = officeWidget?.payslip_visibility !== false;
  const payslipLink = officeWidget?.payslip_link || "https://justlogin.com/";

  const sidebarItemDefinitions = {
    "dashboard": {
      key: "dashboard",
      icon: <DashboardOutlined />,
      label: "Dashboard",
      path: "/"
    },
    "projects": {
      key: "projects",
      icon: <ProjectOutlined />,
      label: "Projects",
      path: "/project-management"
    },
    "bd": {
      key: "bd",
      icon: <SolutionOutlined />,
      label: "BD",
      children: [
      {
        key: "bd-crm-dashboard",
        icon: <ExportOutlined />,
        label: "CRM Dashboard",
        external: true,
        externalUrl: "https://mt-lead-intelligence.netlify.app/leads"
      },
      {
        key: "bd-atlas",
        icon: <ExportOutlined />,
        label: "Atlas",
        external: true,
        externalUrl: "https://atlas.mediatropy.com/"
      }]

    },
    "knowledge-learning": {
      key: "knowledge-learning",
      icon: <BookOutlined />,
      label: "Knowledge",
      children: [
      {
        key: "wiki",
        icon: <FileTextOutlined />,
        label: "Wiki",
        path: "/wiki"
      },
      {
        key: "knowledgebase",
        icon: <BookOutlined />,
        label: "Knowledgebase",
        external: false,
        path: "/knowledgebase"
      },
      {
        key: "trainings",
        icon: <CarryOutOutlined />,
        label: "Learning & Development",
        path: "/training"
      }]

    },
    "knowledgebase": {
      key: "knowledgebase",
      icon: <BookOutlined />,
      label: "Knowledgebase",
      external: false,
      path: "/knowledgebase"
    },
    "wiki": {
      key: "wiki",
      icon: <FileTextOutlined />,
      label: "Wiki",
      path: "/wiki"
    },
    "ai-tools": {
      key: "ai-tools",
      icon: <ToolOutlined />,
      label: "AI Tools",
      path: "/ai-tools"
    },
    "company-directory": {
      key: "company-directory",
      icon: <TeamOutlined />,
      label: "Company Directory",
      children: [
      {
        key: "company-directory-about",
        label: "About Mediatropy",
        icon: <InfoCircleOutlined />,
        path: "/company-directory/about"
      },
      {
        key: "company-directory-office",
        label: "Office",
        icon: <BankOutlined />,
        path: "/company-directory/office"
      },
      {
        key: "company-directory-team",
        label: "Team",
        icon: <UsergroupAddOutlined />,
        path: "/company-directory/team"
      },
      {
        key: "company-directory-department",
        label: "Department",
        icon: <ApartmentOutlined />,
        path: "/company-directory/department"
      },
      {
        key: "company-directory-organization",
        label: "Organization Chart",
        icon: <NodeIndexOutlined />,
        path: "/company-directory/organization"
      },
      vacanciesVisible && {
        key: "company-directory-vacancies",
        label: "Vacancies",
        icon: <SolutionOutlined />,
        external: true,
        externalUrl: vacanciesLink
      }].filter(Boolean)

    },
    "timesheet": {
      key: "timesheet",
      icon: <CalendarOutlined />,
      label: "Timesheet",
      path: "/timesheet"
      // Always use React component, never external link
    },
    "tasks": {
      key: "tasks",
      icon: <CheckSquareOutlined />,
      label: "Tasks",
      path: "/tasks"
    },
    "surveys": {
      key: "surveys",
      icon: <FileTextOutlined />,
      label: "Surveys",
      path: "/surveys"
    },
    "finances": {
      key: "finances",
      icon: <DollarOutlined />,
      label: "Finance",
      children: [
      {
        key: "finance-overview",
        icon: <DollarOutlined />,
        label: "Overview",
        path: "/finances"
      },
      payslipVisible && {
        key: "finance-payslips",
        icon: <FileTextOutlined />,
        label: "Payslips",
        external: true,
        externalUrl: payslipLink
      }].filter(Boolean)
    },
    "announcements": {
      key: "announcements",
      icon: <NotificationOutlined />,
      label: "Announcements",
      path: "/announcements"
      // Remove external link - use React component in all environments
    },
    "newsfeed": {
      key: "newsfeed",
      icon: <ReadOutlined />,
      label: "Newsfeed",
      path: "/newsfeed"
    },
    "notifications": {
      key: "notifications",
      icon: <BellOutlined />,
      label: "Notifications",
      badge: notificationCount,
      path: "/notification"
      // Remove external link - use local route in all environments
    },
    "trainings": {
      key: "trainings",
      icon: <CarryOutOutlined />,
      label: "Learning & Development",
      path: "/training"
    }
  };

  const items = HOME_SIDEBAR_MAIN_KEYS
  .map((key) => sidebarItemDefinitions[key])
  .filter(Boolean);

  // Admin menu with role-based access control
  const buildAdminMenu = () => {
    const sortByLabel = (a, b) => {
      const labelA = typeof a.label === 'string' ? a.label : (a.sortLabel || '');
      const labelB = typeof b.label === 'string' ? b.label : (b.sortLabel || '');
      return labelA.localeCompare(labelB);
    };

    // --- Group: Organization ---
    const orgItems = [];
    if (roleService.hasRole(ROLES.ROLE_HR) && roleService.hasRole(ROLES.ROLE_ACCESS_ALL_OFFICE)) {
      orgItems.push({
        key: "company-settings",
        label: "Company",
        icon: <HomeOutlined />,
        path: '/admin/company',
        external: false
      });
      orgItems.push({
        key: "department-settings",
        label: "Department",
        icon: <ApartmentOutlined />,
        path: '/admin/department',
        external: false
      });
      orgItems.push({
        key: "office-settings",
        label: "Office",
        icon: <BankOutlined />,
        path: '/admin/office',
        external: false
      });
    } else if (roleService.hasRole(ROLES.ROLE_HR)) {
      orgItems.push({
        key: "single-office-settings",
        label: "Office",
        icon: <BankOutlined />,
        path: '/admin/office',
        external: false
      });
    } else if (roleService.canManageTeams() || roleService.isManager()) {
      orgItems.push({
        key: "department-rate-card",
        label: "Billable Rate Card",
        icon: <ApartmentOutlined />,
        path: '/admin/department?tab=roles',
        external: false
      });
    }

    // --- Group: Communication ---
    const commItems = [];
    if (roleService.hasRole(ROLES.ROLE_HR) || roleService.hasRole(ROLES.ROLE_MARCOM)) {
      commItems.push({
        key: "newsfeed-settings",
        label: "Manage Newsfeed",
        icon: <ReadOutlined />,
        path: "/admin/newsfeed"
      });
      commItems.push({
        key: "announcement-settings",
        label: "Announcements",
        icon: <NotificationOutlined />,
        path: "/admin/announcements"
      });
    }
    if (
      (roleService.hasRole(ROLES.ROLE_HR) && roleService.hasRole(ROLES.ROLE_ACCESS_ALL_OFFICE)) ||
      roleService.hasRole(ROLES.ROLE_MARCOM)
    ) {
      const emailChildren = [
        {
          key: "email-viewer",
          label: "Viewer",
          icon: <EyeOutlined />,
          notImplemented: true
        },
        {
          key: "email-banner",
          label: "Banner",
          icon: <PictureOutlined />,
          path: "/settings/email/banner"
        },
        {
          key: "email-signature-template",
          label: "Signature Template",
          icon: <EditOutlined />,
          path: "/settings/email/signature-template"
        }
      ];
      if (!isProduction()) {
        emailChildren.push({
          key: "email-management",
          label: "Template Management",
          icon: <ToolOutlined />,
          path: "/admin/email-management"
        });
      }
      commItems.push({
        key: "email-settings",
        label: "Email",
        icon: <MailOutlined />,
        children: emailChildren
      });
    }
    if (roleService.hasRole(ROLES.ROLE_MARCOM)) {
      commItems.push({
        key: "admin-qna",
        label: "Q&A",
        icon: <QuestionCircleOutlined />,
        path: "/admin/qna"
      });
    }
    if (roleService.hasRole(ROLES.ROLE_SUPER_ADMIN) || roleService.hasRole(ROLES.ROLE_OWNER)) {
      commItems.push({
        key: "admin-notifications",
        label: "Notification Management",
        icon: <BellOutlined />,
        path: "/admin/notifications"
      });
    }

    // --- Group: Content ---
    const contentItems = [];
    if (roleService.hasRole(ROLES.ROLE_MARCOM) || roleService.getCurrentUser()?.feature_document_management) {
      contentItems.push({
        key: "knowledgebase-settings",
        label: "Knowledgebase",
        icon: <BookOutlined />,
        path: "/admin/knowledgebase"
      });
    }
    if (canManageWikiSettings()) {
      contentItems.push({
        key: "wiki-settings",
        label: "Wiki Settings",
        icon: <BookOutlined />,
        path: "/wiki/settings"
      });
    }
    contentItems.push({
      key: "hr-trainings",
      label: "Learning & Development",
      icon: <BookOutlined />,
      path: "/admin/hr/trainings",
      external: false
    });

    // --- Group: People (HR submenu — internal order unchanged) ---
    const hrChildren = [];
    if (roleService.canManageHR()) {
      hrChildren.push({
        key: "hr-dashboard",
        label: <span>Dashboard <Tag color="orange" style={{ fontSize: '10px', lineHeight: '16px', padding: '0 4px', marginLeft: '4px', borderRadius: '3px' }}>Beta</Tag></span>,
        sortLabel: "Dashboard",
        icon: <DashboardOutlined />,
        path: "/admin/hr/dashboard"
      });
    }
    if (roleService.hasRole(ROLES.ROLE_FINANCE) || roleService.isManager()) {
      hrChildren.push({
        key: "hr-employees",
        label: "Employees",
        icon: <UserOutlined />,
        path: "/admin/hr/employees",
        external: false
      });
    }
    if (roleService.canManageAppraisals()) {
      hrChildren.push({
        key: "hr-appraisals",
        label: "Appraisals",
        icon: <FileTextOutlined />,
        path: "/admin/hr/appraisals"
      });
    }
    if (roleService.canManageHR()) {
      hrChildren.push({
        key: "hr-surveys",
        label: "Surveys",
        icon: <FileTextOutlined />,
        path: "/admin/hr/surveys"
      });
      hrChildren.push({
        key: "hr-workplace-feedback",
        label: "Workplace Feedback",
        icon: <MessageOutlined />,
        path: "/admin/hr/workplace-feedback"
      });
    }
    if (roleService.hasRole(ROLES.ROLE_HR) || roleService.isManager()) {
      hrChildren.push({
        key: "hr-leaves",
        label: "Leaves (Holidays)",
        icon: <CalendarOutlined />,
        path: "/admin/hr/leaves"
      });
    }
    hrChildren.push({
      key: "hr-recruitment",
      label: "Recruitment",
      icon: <SolutionOutlined />,
      path: "/recruit"
    });
    if (roleService.hasRole(ROLES.ROLE_FINANCE)) {
      hrChildren.push({
        key: "hr-asset",
        label: "Computers & Equipment",
        icon: <DesktopOutlined />,
        path: "/admin/hr/asset"
      });
    }
    if (roleService.canManageHR()) {
      hrChildren.push({
        key: "hr-awards",
        label: "Awards",
        icon: <TrophyOutlined />,
        path: "/admin/hr/awards"
      });
    }
    if (roleService.hasRole(ROLES.ROLE_HR) || roleService.hasRole(ROLES.ROLE_MANAGEMENT) || roleService.hasRole(ROLES.ROLE_TEAM_LEADER)) {
      hrChildren.push({
        key: "hr-flexible-work",
        label: "Flexible Work Arrangement",
        icon: <ClockCircleOutlined />,
        path: "/admin/hr/flexible-work",
        notImplemented: true
      });
    }
    if (roleService.hasRole(ROLES.ROLE_HR) || roleService.isManager()) {
      hrChildren.push({
        key: "hr-working-location",
        label: "MT Anywhere+",
        icon: <HomeOutlined />,
        path: "/admin/hr/working-location"
      });
    }

    const peopleItems = [];
    if (hrChildren.length > 0) {
      peopleItems.push({
        key: "hr",
        label: "HR",
        icon: <TeamOutlined />,
        children: hrChildren
      });
    }

    const canAccessSystemTools =
      roleService.hasRole(ROLES.ROLE_CAN_SWITCH_USER) ||
      roleService.hasRole(ROLES.ROLE_OWNER) ||
      roleService.hasRole(ROLES.ROLE_SUPER_ADMIN);
    // Every authenticated employee can submit a manual supplier bill. The
    // page itself keeps Aspire, staging, and card-access tabs role-scoped.
    const canAccessVendorBillProcessing = true;

    // --- Group: Finance ---
    const financeItems = [];
    if (
      roleService.hasRole(ROLES.ROLE_FINANCE) ||
      roleService.hasSpecialAccess("finance_report")
    ) {
      financeItems.push({
        key: "admin-finances",
        label: "Finance",
        icon: <DollarOutlined />,
        path: "/admin/finances"
      });
    }

    if (canAccessVendorBillProcessing) {
      financeItems.push({
        key: "vendor-bill-processing",
        label: "Vendor Bill Processing",
        icon: <DollarOutlined style={{ color: '#FFA940', fontWeight: 'bold' }} />,
        path: "/admin/vendor-bill-processing"
      });
    }

    // --- Group: AI & Automation ---
    const aiItems = [];
    if (
      (roleService.hasRole(ROLES.ROLE_HR) && roleService.hasRole(ROLES.ROLE_ACCESS_ALL_OFFICE)) ||
      roleService.hasRole(ROLES.ROLE_MARCOM)
    ) {
      aiItems.push({
        key: "ai-prompt-management",
        label: "AI Prompts",
        icon: <RobotOutlined />,
        path: "/admin/ai-prompts"
      });
    }

    // --- Group: System (owner/super-admin tools) ---
    const systemItems = [];

    if (canAccessSystemTools) {
      systemItems.push({
        key: "activity-log",
        label: "Activity Log",
        icon: <ClockCircleOutlined style={{ color: '#FFA940', fontWeight: 'bold' }} />,
        path: "/admin/activity-log"
      });
      systemItems.push({
        key: "api-key-management",
        label: "API Key Management",
        icon: <KeyOutlined style={{ color: '#FFA940', fontWeight: 'bold' }} />,
        path: "/admin/api-key-management"
      });
      systemItems.push({
        key: "api-usage",
        label: "API Usage Analysis",
        icon: <ApiOutlined style={{ color: '#FFA940', fontWeight: 'bold' }} />,
        path: "/admin/api-usage"
      });
    }

    if (canAccessSystemTools) {
      systemItems.push({
        key: "process-experimentation",
        label: "Process Improvement",
        icon: <NodeIndexOutlined style={{ color: '#FFA940', fontWeight: 'bold' }} />,
        path: "/admin/process-improvement"
      });
      systemItems.push({
        key: "admin-user-impersonator",
        label: "User Impersonator",
        icon: <UserSwitchOutlined style={{ color: '#FFA940', fontWeight: 'bold' }} />,
        path: "/admin/user-impersonator"
      });
    }


    // --- Assemble grouped menu ---
    const adminChildren = [];

    const groups = [
      { label: 'Organization', items: orgItems },
      { label: 'Communication', items: commItems },
      { label: 'Knowledge', items: contentItems, preserveOrder: true },
      { label: 'People', items: peopleItems },
      { label: 'Finance', items: financeItems },
      { label: 'AI & Automation', items: aiItems },
      { label: 'System', items: systemItems },
    ];

    groups.forEach(({ label, items, preserveOrder }) => {
      if (items.length > 0) {
        if (!preserveOrder) {
          items.sort(sortByLabel);
        }
        adminChildren.push({
          key: `admin-group-${label.toLowerCase().replace(/\s+/g, '-')}`,
          label,
          children: items
        });
      }
    });

    // --- Fixed bottom items (no group header) ---
    if (!isProduction()) {
      adminChildren.push({
        key: "manage-account",
        label: "Manage Account",
        icon: <SettingOutlined />,
        path: "/admin/manage-account"
      });
    }

    adminChildren.push({
      key: "logout",
      label: "Logout",
      icon: <LogoutOutlined style={{ color: '#ff4d4f', fontWeight: 'bold' }} />
    });

    return {
      key: "admin",
      icon: <SettingOutlined />,
      label: "Admin",
      children: adminChildren
    };
  };

  const adminMenu = buildAdminMenu();

  // Process menu items to add external link icons and Link wrappers
  const processMenuItem = (item, isAdminMenu = false) => {
    // Destructure to exclude custom properties that shouldn't be passed to DOM
    // eslint-disable-next-line no-unused-vars
    const { external, externalUrl, badge, path, sortLabel, ...cleanItem } = item;
    const processedItem = { ...cleanItem };

    // Add arrow indicator to icon for items with children when collapsed
    if (item.children && collapsed && item.icon) {
      processedItem.icon =
      <span className="submenu-icon-with-arrow" style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                    {item.icon}
                    <span className="submenu-arrow" style={{
          fontSize: '8px',
          opacity: 0.6,
          color: '#1b1b1b',
          marginLeft: '-2px'
        }}>▸</span>
                </span>;

    }

    // Get the base label content
    let labelContent = item.label;

    // Check if this is a privileged menu item (User Impersonator, Activity Log, API Key Management or API Usage)
    const isPrivileged = item.key === 'admin-user-impersonator' || item.key === 'activity-log' || item.key === 'api-key-management' || item.key === 'api-usage';

    // Check if this is the logout item
    const isLogout = item.key === 'logout';

    // Add external link icon if needed
    if (external) {
      labelContent =
      <span>
                    {item.label} <ExportOutlined style={{ fontSize: '11px', marginLeft: '4px', opacity: 0.7 }} />
                </span>;

    } else if (badge !== undefined && badge > 0) {
      labelContent =
      <Badge count={badge} overflowCount={badge} offset={[18, 0]}>
                    <span>{item.label}</span>
                </Badge>;

    }

    // Add privileged menu badge if needed - style the text in orange
    if (isPrivileged) {
      labelContent =
      <span style={{ color: '#FFA940', fontWeight: 500 }}>
                    {labelContent}
                </span>;

    }

    // Style logout in red
    if (isLogout) {
      labelContent =
      <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
                    {labelContent}
                </span>;

    }

    // For admin menu items, use anchor tags with href to enable native browser context menu
    // For non-admin items, use Link component with existing behavior
    if (path && !external && item.key !== 'logout' && !item.children && !item.notImplemented) {
      if (isAdminMenu) {
        // Admin menu items: use anchor tag with href for native context menu
        processedItem.label =
        <a
          href={path}
          style={{
            color: 'inherit',
            textDecoration: 'none'
          }}
          onClick={(e) => {
            // Allow default for right-click, ctrl+click, cmd+click, middle-click
            if (e.ctrlKey || e.metaKey || e.button === 1 || e.button === 2) {
              return; // Let browser handle it
            }
            // For normal left-click, prevent default and use custom navigation
            e.preventDefault();
            navigate(path);
          }}>
          
                        {labelContent}
                    </a>;

      } else {
        // Non-admin menu items: use Link component
        processedItem.label =
        <Link
          to={path}
          style={{
            color: 'inherit',
            textDecoration: 'none'
          }}
          onClick={(e) => {
            // Allow default Link behavior for right-click, ctrl+click, middle-click
            if (e.ctrlKey || e.metaKey || e.button === 1) {
              return; // Let Link handle it
            }
            // For normal clicks, prevent default and let handleMenuClick manage navigation
            e.preventDefault();
          }}>
          
                        {labelContent}
                    </Link>;

      }
    } else if (external && externalUrl && !item.children) {
      // For external links, use regular anchor tag to enable native context menu
      const fullUrl = resolveExternalUrl(externalUrl);
      processedItem.label =
      <a
        href={fullUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: 'inherit',
          textDecoration: 'none'
        }}
        onClick={(e) => {
          // Allow default for right-click, ctrl+click, cmd+click, middle-click
          if (e.ctrlKey || e.metaKey || e.button === 1 || e.button === 2) {
            return; // Let browser handle it
          }
          // For normal left-click, prevent default and use custom handler
          e.preventDefault();
          e.stopPropagation();
          window.open(fullUrl, '_blank');
        }}>
        
                    {labelContent}
                </a>;

    } else {
      processedItem.label = labelContent;
    }

    // Recursively process children, passing down the isAdminMenu flag
    if (item.children) {
      processedItem.children = item.children.map((child) => processMenuItem(child, isAdminMenu));
    }

    return processedItem;
  };

  const handleMenuClick = (e) => {
    // Helper to find menu item by key in nested menus
    const findMenuItem = (items, key) => {
      for (const item of items) {
        if (item.key === key) return item;
        if (item.children) {
          const found = findMenuItem(item.children, key);
          if (found) return found;
        }
      }
      return null;
    };

    // Check if this is a not implemented item first
    const allItems = [...items, adminMenu];
    const foundItem = findMenuItem(allItems, e.key);
    if (foundItem && foundItem.notImplemented) {
      const labelText = typeof foundItem.label === 'string' ?
      foundItem.label :
      foundItem.label?.props?.children?.[0] || 'This feature';
      message.info(`${labelText} is not yet available in the new interface.`);
      closeNavigationMenu();
      return;
    }

    // Check if this is an external link anywhere in the menu tree
    if (foundItem && foundItem.external) {
      window.open(resolveExternalUrl(foundItem.externalUrl), '_blank');
      closeNavigationMenu();
      return;
    }

    // Check if this is an external link in admin menu
    const checkAdminMenu = (menu, key) => {
      if (menu.key === key && menu.external) {
        return menu;
      }
      if (menu.children) {
        for (const child of menu.children) {
          const found = checkAdminMenu(child, key);
          if (found) return found;
        }
      }
      return null;
    };

    const adminMenuItem = checkAdminMenu(adminMenu, e.key);
    if (adminMenuItem && adminMenuItem.external) {
      window.open(resolveExternalUrl(adminMenuItem.externalUrl), '_blank');
      closeNavigationMenu();
      return;
    }

    switch (e.key) {
      case "logout":
        // Call backend logout endpoint for activity logging
        (async () => {
          try {
            await nodeAPI.post('/auth/logout');
          } catch (error) {
            // Log error but continue with logout
            logger.error('Logout API error:', error);
          }

          // Proceed with client-side logout
          dispatch({ type: "logout" });
          // Clear all auth cookies
          cookies.remove("authToken", cookieOptions);
          cookies.remove("tokenR", cookieOptions);
          cookies.remove("userR", cookieOptions);
          cookies.remove("userRId", cookieOptions);
          navigate("/login");
        })();
        break;
      case "dashboard":
        navigate("/");
        break;
      case "recruitment":
        window.location.href = "/recruitment";
        break;
      case "appraisals":
        navigate("/appraisal");
        break;
      // Top level navigation
      case "knowledgebase":
        // In non-production, navigate to the React route
        navigate("/knowledgebase");
        break;
      case "wiki-settings":
        navigate("/wiki/settings");
        break;
      case "analytics":
        navigate("/analytics");
        break;
      case "ai-tools":
        navigate("/ai-tools");
        break;
      case "chatbot":
        navigate("/chatbot");
        break;
      case "timesheet":
        navigate("/timesheet");
        break;
      case "projects":
        navigate("/project-management");
        break;
      case "tasks":
        navigate("/tasks");
        break;
      case "surveys":
        navigate("/surveys");
        break;
      case "wiki":
        navigate("/wiki");
        break;
      case "finance-overview":
      case "finances":
        navigate("/finances");
        break;
      case "newsfeed":
        navigate("/newsfeed");
        break;
      case "announcements":
        navigate("/announcements");
        break;
      case "notifications":
        navigate("/notification");
        break;
      case "trainings":
        navigate("/training");
        break;
      // Company Directory submenu
      case "company-directory-about":
        navigate("/company-directory/about");
        break;
      case "company-directory-office":
        navigate("/company-directory/office");
        break;
      case "company-directory-team":
        navigate("/company-directory/team");
        break;
      case "company-directory-department":
        navigate("/company-directory/department");
        break;
      case "company-directory-organization":
        navigate("/company-directory/organization");
        break;
      // Admin sections
      case "hr-employees":
        navigate("/admin/hr/employees");
        break;
      case "hr-appraisals":
        navigate("/admin/hr/appraisals");
        break;
      case "hr-surveys":
        navigate("/admin/hr/surveys");
        break;
      case "hr-workplace-feedback":
        navigate("/admin/hr/workplace-feedback");
        break;
      case "hr-leaves":
        navigate("/admin/hr/leaves");
        break;
      case "hr-recruitment":
        // Navigate to /recruit route
        navigate("/recruit");
        break;
      case "hr-asset":
        navigate("/admin/hr/asset");
        break;
      case "hr-trainings":
        navigate("/admin/hr/trainings");
        break;
      case "hr-flexible-work":
        navigate("/admin/hr/flexible-work");
        break;
      case "hr-working-location":
        navigate("/admin/hr/working-location");
        break;
      case "admin-finances":
        navigate("/admin/finances");
        break;
      case "admin-qna":
        navigate("/admin/qna");
        break;
      case "admin-user-impersonator":
        navigate("/admin/user-impersonator");
        break;
      case "activity-log":
        navigate("/admin/activity-log");
        break;
      case "api-key-management":
        navigate("/admin/api-key-management");
        break;
      case "email-management":
        navigate("/admin/email-management");
        break;
      case "ai-prompt-management":
        navigate("/admin/ai-prompts");
        break;
      case "api-usage":
        navigate("/admin/api-usage");
        break;
      case "vendor-bill-processing":
        navigate("/admin/vendor-bill-processing");
        break;
      case "process-experimentation":
        navigate("/admin/process-improvement");
        break;
      case "newsfeed-settings":
        navigate("/admin/newsfeed");
        break;
      case "manage-account":
        navigate("/admin/manage-account");
        break;
      case "email-banner":
        // Navigate to /settings/email/banner route
        navigate("/settings/email/banner");
        break;
      case "email-signature-template":
        // Navigate to /settings/email/signature-template route
        navigate("/settings/email/signature-template");
        break;
      case "office-settings":
      case "single-office-settings":
        // Navigate to /admin/office route
        navigate("/admin/office");
        break;
      case "department-settings":
        // Navigate to /admin/department route
        navigate("/admin/department");
        break;
      case "department-rate-card":
        navigate("/admin/department?tab=roles");
        break;
      case "company-settings":
        // Navigate to /admin/company route
        navigate("/admin/company");
        break;
      default:
        logger.warn("Unhandled menu key:", e.key);
    }

    closeNavigationMenu();
  };

  // Extract selected key from pathname
  const getSelectedKey = () => {
    return getHomeSiderMainSelectedKey(location.pathname);
  };

  // Extract default open keys for submenus
























































  const handleWorkStatusChange = (status) => {
    setWorkStatus(status);
    localStorage.setItem("userWorkStatus", status);

    // Call API to update status
    nodeAPI
    .put("/user/status", { status })
    .then(() => {

      // Status updated successfully - no toast needed
    }).catch((error) => {
      logger.error("Failed to update work status:", error);
      message.error("Failed to update work status");
    });
  };

  // Filter work statuses to show only common ones
  const filteredStatuses = {
    working: USER_STATUS.working,
    working_home: USER_STATUS.working_home,
    working_aboard: USER_STATUS.working_aboard,
    off: USER_STATUS.off,
    leave: USER_STATUS.leave
  };

  const workStatusItems = Object.entries(filteredStatuses).map(
    ([key, config]) => ({
      key,
      label:
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: config.color,
            display: "inline-block"
          }} />
        
                    {config.label}
                </div>,

      onClick: () => handleWorkStatusChange(key)
    })
  );

  const closeNavigationMenu = () => {
    setOpenKeys([]);
    resetMenuInstance();

    if (
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 768px)").matches
    ) {
      setCollapsed(true);
    }
  };

  const adminSelectedKey = getHomeSiderAdminSelectedKey(location.pathname, location.search, {
    useDepartmentRateCard:
      location.search.includes("tab=roles") &&
      !(roleService.hasRole(ROLES.ROLE_HR) && roleService.hasRole(ROLES.ROLE_ACCESS_ALL_OFFICE))
  });
  const mainSelectedKey = getSelectedKey();
  const mainMenuSelectedKeys = getHomeSiderMainMenuSelectedKeys({
    mainSelectedKey,
    adminSelectedKey,
    isMobileSidebar
  });
  const mainMenuOpenKeys = getHomeSiderMainMenuOpenKeys(openKeys, isMobileSidebar);
  const adminMenuOpenKeys = getHomeSiderAdminMenuOpenKeys(openKeys);
  const mainMenuItems = isMobileSidebar ?
  [
  ...items.map((item) => processMenuItem(item)),
  processMenuItem(adminMenu, true)]
  :
  items.map((item) => processMenuItem(item));

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={225}
      className={`home-sider ${!animationEnabled ? 'sidebar-no-animation' : ''}`}
      style={{
        background: "#fff",
        position: "relative",
        display: "flex",
        flexDirection: "column"
      }}>
            <button
        type="button"
        className="home-sider-mobile-close"
        aria-label="Close menu"
        onClick={() => setCollapsed(true)}>
                <CloseOutlined />
            </button>
      
            {/* Logo */}
            <div
        className="home-sider-logo"
        style={{
          padding: collapsed ? "8px" : "16px",
          textAlign: "center"
          // borderBottom: "1px solid #f0f0f0"
        }}>
        
                {!collapsed ?
        <Link
          to="/"
          onClick={(e) => {
            // Allow default Link behavior for right-click, ctrl+click, middle-click
            if (e.ctrlKey || e.metaKey || e.button === 1) {
              return; // Let Link handle it
            }
            // For normal clicks, use the existing handler
            e.preventDefault();
            handleMenuClick({ key: "dashboard" });
          }}>
          
                        <img
            src={logoImage}
            alt="Mediatropy"
            style={{
              width: "130px",
              height: "auto",
              cursor: "pointer"
            }} />
          
                    </Link> :

        <Link
          to="/"
          onClick={(e) => {
            // Allow default Link behavior for right-click, ctrl+click, middle-click
            if (e.ctrlKey || e.metaKey || e.button === 1) {
              return; // Let Link handle it
            }
            // For normal clicks, use the existing handler
            e.preventDefault();
            handleMenuClick({ key: "dashboard" });
          }}>
          
                        <img
            src={faviconImage}
            alt="Mediatropy"
            style={{
              width: 32,
              height: 32,
              margin: "0 auto",
              cursor: "pointer"
            }} />
          
                    </Link>
        }
            </div>

            {/* Follow Us Dropdown */}
            {!collapsed &&
      <div
        className="home-sider-follow"
        style={{
          padding: "0 16px 16px",
          textAlign: "center",
          borderBottom: "1px solid #f0f0f0"
        }}>
        
                    <Dropdown
          menu={{
            items: [
            {
              key: "facebook",
              label: "Facebook",
              icon:
              <FacebookOutlined
                style={{ color: "#FED230" }} />,


              onClick: () =>
              window.open(
                "https://www.facebook.com/mediatropysg/",
                "_blank"
              )
            },
            {
              key: "instagram",
              label: "Instagram",
              icon:
              <InstagramOutlined
                style={{ color: "#FED230" }} />,


              onClick: () =>
              window.open(
                "https://www.instagram.com/mediatropy/",
                "_blank"
              )
            },
            {
              key: "linkedin",
              label: "LinkedIn",
              icon:
              <LinkedinOutlined
                style={{ color: "#FED230" }} />,


              onClick: () =>
              window.open(
                "https://www.linkedin.com/company/mediatropy",
                "_blank"
              )
            },
            {
              key: "twitter",
              label: "Twitter",
              icon:
              <TwitterOutlined
                style={{ color: "#FED230" }} />,


              onClick: () =>
              window.open(
                "https://twitter.com/mediatropy",
                "_blank"
              )
            },
            {
              key: "youtube",
              label: "YouTube",
              icon:
              <YoutubeOutlined
                style={{ color: "#FED230" }} />,


              onClick: () =>
              window.open(
                "https://www.youtube.com/@mediatropy",
                "_blank"
              )
            }]

          }}
          trigger={["click"]}
          placement="bottomLeft">
          
                        <Button
            style={{
              width: "100%",
              borderRadius: "20px",
              border: "1px solid #e0e0e0",
              backgroundColor: "#fff",
              color: "#333",
              fontSize: "14px"
            }}>
            
                            Follow Us
                        </Button>
                    </Dropdown>
                </div>
      }

            {/* Help & Feedback Button */}
            <div className="home-sider-help-feedback">
            <HelpFeedbackButton
        collapsed={collapsed}
        onClick={() => setFeedbackModalVisible(true)} />
            </div>

            {voiceAssistantEnabled &&
      <div style={{ padding: collapsed ? "8px" : "8px 16px" }}>
                <button
          type="button"
          onClick={() => setVoiceAssistantVisible(true)}
          title="Voice Assistant"
          style={{
            width: "100%",
            minHeight: 36,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-start",
            gap: 10,
            padding: collapsed ? "8px" : "8px 12px",
            border: "1px solid #e0e0e0",
            borderRadius: 8,
            background: "#fff",
            color: "#262626",
            cursor: "pointer",
            fontSize: 14
          }}>
                    <AudioOutlined />
                    {!collapsed && <span>Voice Assistant</span>}
                </button>
              </div>
      }
      
            <HelpFeedbackModal
        visible={feedbackModalVisible}
        onClose={() => setFeedbackModalVisible(false)} />

            {voiceAssistantEnabled && voiceAssistantVisible &&
      <VoiceAgent
        agentId="task"
        visible={true}
        onClose={() => setVoiceAssistantVisible(false)} />
      }

            <div className="home-sider-screen-nav">
                <ScreenQuickNav collapsed={collapsed} />
            </div>
      

            {/* User Avatar and Status */}
            <div
        className="home-sider-user-card"
        style={{
          padding: collapsed ? "16px 8px" : "16px",
          borderBottom: "1px solid #f0f0f0",
          textAlign: "center"
        }}>
        
                <Avatar
          size={collapsed ? 32 : 48}
          src={profilePicture}
          icon={!profilePicture && <UserOutlined />}
          style={{
            backgroundColor: !profilePicture ? "#FED230" : undefined,
            border: profilePicture ? "2px solid #FED230" : undefined,
            cursor: "pointer"
          }}
          onClick={() => {
            navigate("/profile");
            closeNavigationMenu();
          }}>
          
                    {loadingProfile &&
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }} />

          }
                </Avatar>
                {!collapsed &&
        <>
                        <div style={{ marginTop: 8 }}>
                            <div
              className="home-sider-user-name"
              style={{
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                textDecoration: "underline"
              }}
              onClick={() => {
                navigate("/profile");
                closeNavigationMenu();
              }}>
              
                                {userName}
                            </div>
                        </div>
                        <div
            style={{
              marginTop: 4,
              width: "100%",
              display: "flex",
              justifyContent: "center"
            }}>
            
                            <Dropdown
              menu={{ items: workStatusItems }}
              placement="bottomLeft"
              trigger={["click"]}
              getPopupContainer={(trigger) =>
              trigger.parentElement
              }>
              
                                <Tag
                color={
                USER_STATUS[workStatus]?.color ||
                USER_STATUS.working_home.color
                }
                style={{
                  cursor: "pointer",
                  width: "100%",
                  textAlign: "center",
                  margin: 0,
                  padding: "4px 16px",
                  fontSize: "13px",
                  borderRadius: "20px"
                }}>
                
                                    {USER_STATUS[workStatus]?.label ||
                USER_STATUS.working_home.label}
                                </Tag>
                            </Dropdown>
                        </div>
                    </>
        }
            </div>

            <div
        className="home-sider-menu-shell"
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0 // Important for flex child scrolling
        }}>
        
                {/* Scrollable Main Menu Container */}
                <div
          className="home-sider-menu-scroll"
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            minHeight: 0
          }}>
          
            <Menu
            key={`home-sider-main-${menuInstanceKey}`}
            mode={menuMode}
            selectedKeys={mainMenuSelectedKeys}
            openKeys={mainMenuOpenKeys}
            onOpenChange={(keys) => {
              if (isMobileSidebar) {
                setOpenKeys(keys);
                return;
              }

              setOpenKeys([...keys, ...adminMenuOpenKeys]);
            }}
            style={{ borderRight: 0 }}
            items={mainMenuItems}
            onClick={handleMenuClick} />
          
                </div>

                {/* Fixed Admin Menu at Bottom - Visible to all users */}
                {!isMobileSidebar &&
                <div
          className="home-sider-admin-menu"
          style={{
            borderTop: "2px solid #f0f0f0",
            paddingTop: "8px",
            marginTop: "8px",
            paddingBottom: "56px", // Space for collapse button (48px height + 8px padding)
            backgroundColor: "#fff"
          }}>
          
            <Menu
            key={`home-sider-admin-${menuInstanceKey}`}
            mode={menuMode}
            selectedKeys={[adminSelectedKey].filter(Boolean)}
            openKeys={adminMenuOpenKeys}
            onOpenChange={(keys) => {
              // Handle admin menu separately
              const adminKeys = keys.filter(
                (key) =>
                key === "admin" ||
                key.startsWith("admin-") ||
                key === "hr" ||
                key === "email-settings"
              );
              const otherKeys = openKeys.filter(
                (key) =>
                key !== "admin" &&
                !key.startsWith("admin-") &&
                key !== "hr" &&
                key !== "email-settings"
              );
              setOpenKeys([...otherKeys, ...adminKeys]);
            }}
            style={{ borderRight: 0 }}
            items={[adminMenu].map((item) => processMenuItem(item, true))}
            onClick={handleMenuClick} />
          
                </div>
                }
            </div>

            {/* Collapse button at the very bottom */}
            <div
        className="home-sider-collapse-toggle"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: collapsed ? "80px" : "225px",
          height: "48px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderTop: "1px solid #f0f0f0",
          cursor: "pointer",
          backgroundColor: "#fff",
          transition: animationEnabled ? "all 0.3s" : "none",
          zIndex: 1000
        }}
        onClick={() => toggleCollapsed(true)}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#f0f0f0";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "#fff";
        }}>
        
                <div style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          color: "#666",
          fontSize: "14px"
        }}>
                    {collapsed ?
          <MenuUnfoldOutlined style={{ fontSize: "18px" }} /> :

          <>
                            <MenuFoldOutlined style={{ fontSize: "18px" }} />
                            <span style={{ fontSize: "12px", fontWeight: 500 }}>Collapse Menu</span>
                        </>
          }
                </div>
            </div>

        </Sider>);

};

export default HomeSider;
